import {inRange} from 'lodash';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Col, Row, UiInput, UiQuestionnaireLayout, UiText} from '~/components/elements';
import {BMI_18_5, BlockScreenType, BMI_27, WEIGHT_LOSS_KEYS, BMI_25} from '~/constants/medical-questionnaires';
import {useInput} from '~/hooks';
import {setNextQuestionnaire, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {trackingSorryPageMisc} from '~/redux/questionnaires/thunk';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';

const Questionnaire7Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);

  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(i => i.key === WEIGHT_LOSS_KEYS.BMI_CALCULATOR);
  const {wpAuthUser, mdqID} = medicalQuestionnaire;
  const methodKey = useSelector((state: RootState) => state.questionnaires.methodKey);
  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[WEIGHT_LOSS_KEYS.BMI_CALCULATOR]?.data,
  );
  const trackWpUserAuth = useSelector((state: RootState) => state.questionnaires.trackUserAuth);
  const {inputs, setInputs} = useInput({
    height: currentAnswer?.height || wpAuthUser?.height || '',
    weight: currentAnswer?.weight || wpAuthUser?.weight || '',
    bmi: currentAnswer?.bmi || '',
  });

  const calculateBmi = () => {
    return Number((inputs.weight / Math.pow(inputs.height / 100, 2)).toFixed(1));
  };
  const isValid = inputs?.weight && inputs?.height;

  const onClickSubmit = async () => {
    const userBmi = calculateBmi();
    dispatch(
      setQuestionnaireAnswer({
        key: WEIGHT_LOSS_KEYS.BMI_CALCULATOR,
        index: questionnaire?.index || '',
        data: {height: Number(inputs?.height), weight: Number(inputs?.weight), bmi: userBmi},
      }),
    );
    if (userBmi <= BMI_18_5) {
      await dispatch(
        trackingSorryPageMisc({
          methodKey,
          screen: WEIGHT_LOSS_KEYS.BMI_UNDERWEIGHT,
          blockScreenType: BlockScreenType.BMI_UNDER_27,
          collectData: {},
          mdqKey: mdqID || '',
          wpAuthUser: trackWpUserAuth,
        }),
      );
      return dispatch(setNextQuestionnaire({value: WEIGHT_LOSS_KEYS.BMI_UNDERWEIGHT}));
    }
    if (userBmi > BMI_18_5 && userBmi < BMI_25) {
      await dispatch(
        trackingSorryPageMisc({
          methodKey,
          screen: WEIGHT_LOSS_KEYS.BMI_HEALTHY_BLOCK,
          blockScreenType: BlockScreenType.BMI_UNDER_27,
          collectData: {},
          mdqKey: mdqID || '',
          wpAuthUser: trackWpUserAuth,
        }),
      );
      return dispatch(setNextQuestionnaire({value: WEIGHT_LOSS_KEYS.BMI_HEALTHY_BLOCK}));
    }
    if (userBmi >= BMI_25 && userBmi < BMI_27) {
      await dispatch(
        trackingSorryPageMisc({
          methodKey,
          screen: WEIGHT_LOSS_KEYS.BMI_HEALTHY_NEXT,
          blockScreenType: BlockScreenType.BMI_UNDER_27,
          collectData: {},
          mdqKey: mdqID || '',
          wpAuthUser: trackWpUserAuth,
        }),
      );
      return dispatch(setNextQuestionnaire({value: WEIGHT_LOSS_KEYS.BMI_HEALTHY_NEXT}));
    }
    if (userBmi >= BMI_27) {
      return dispatch(setNextQuestionnaire({value: WEIGHT_LOSS_KEYS.OBESITY_RELATED_CONDITIONS}));
    }
  };

  const renderRiskDesc = () => {
    if (isValid) {
      const bmi = calculateBmi();
      const currentRiskLevel = questionnaire?.riskLevels?.find(
        value => inRange(bmi, value.bmiRange[0], value.bmiRange[1]) || value.bmiRange.includes(bmi),
      );

      return (
        <>
          <UiText fontWeight={700} fontSize={12} mb={4}>
            {currentRiskLevel?.label}
          </UiText>
          <UiText fontSize={12}>{currentRiskLevel?.description}</UiText>
        </>
      );
    }
    return null;
  };

  const renderBmi = () => {
    if (isValid) {
      return (
        <UiText ml={2} variant="mdqTitle">
          {calculateBmi()}
        </UiText>
      );
    }
    return null;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value} = e.target;
    const onlyNumbersAndOneDotRegex = /^\d*\.?\d*$/;
    if (onlyNumbersAndOneDotRegex.test(value)) {
      setInputs({[name]: value});
    }
  };

  return (
    <UiQuestionnaireLayout
      backIcon
      handleOnNext={onClickSubmit}
      disableNext={!isValid}
      actions={questionnaire?.actions}
      checkoutStep={questionnaire?.checkoutStep}
    >
      <Col alignItems="center" textAlign="center">
        <UiText variant="mdqTitle" my={10}>
          {questionnaire?.questions?.default}
        </UiText>
        <Row style={{alignItems: 'flex-end'}} mb={10}>
          <UiInput
            name="height"
            type="text"
            style={{border: 'none', outline: 'none', borderBottom: '1px solid gray', maxWidth: '60px'}}
            value={inputs.height}
            onChange={handleChange}
          />
          <UiText fontSize="md" ml={5}>
            cm
          </UiText>
        </Row>
        <UiText variant="mdqTitle" mb={10}>
          {questionnaire?.questions?.sub}
        </UiText>
        <Row style={{alignItems: 'flex-end'}} mb={10}>
          <UiInput
            name="weight"
            type="text"
            style={{border: 'none', outline: 'none', borderBottom: '1px solid gray', maxWidth: '60px'}}
            value={inputs.weight}
            onChange={handleChange}
          />
          <UiText fontSize="md" ml={5}>
            kg
          </UiText>
        </Row>
        <Row style={{alignItems: 'center'}} mb={10}>
          <UiText fontSize="lg">Your BMI is</UiText>
          {renderBmi()}
        </Row>
        <Col>{renderRiskDesc()}</Col>
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire7Screen;
